import type { IQuestion } from '@repo/types/questions/Question.interface';
import type {
  AnswerQuestionMultiple,
  AnswerUserQuestionMultiple,
  ExportAnswerMultiple,
  FeedbackAnswerQuestionMultiple,
  QuestionMultipleContent,
  QuestionMultipleData,
  QuestionMultipleSerialized,
} from '@repo/types/questions/QuestionMultiple';
import type { CorrectType } from '@repo/types/quiz';

import { QuestionType } from '@repo/types/enums/questionType';

export class QuestionMultiple implements IQuestion {
  readonly type: QuestionType;
  readonly content: string;
  private answers: AnswerQuestionMultiple[];
  private readonly points: number = 0;
  private selectedAnswer: AnswerUserQuestionMultiple | null = null;
  private marked: FeedbackAnswerQuestionMultiple | null = null;
  constructor(data: QuestionMultipleData, selectedAnswer: AnswerUserQuestionMultiple | null, marked: FeedbackAnswerQuestionMultiple | null) {
    this.type = QuestionType.MULTIPLE;
    this.content = data.content;
    this.answers = data.answers;
    this.selectedAnswer = selectedAnswer;
    this.marked = marked;
    this.points = data.points;
  }

  getContent(): QuestionMultipleContent {
    return {
      content: this.content,
      answers: this.answers,
    };
  }

  getMaxPoints(): number {
    return this.points;
  }

  getPoints(): number {
    if (this.marked == null || this.marked.answers == null) return 0;

    return this.marked.answers.reduce((sum, marked) => {
      sum += marked.points ?? 0;
      return sum;
    }, 0);
  }

  setAnswer(ids: string[]): void {
    const answers: AnswerUserQuestionMultiple = [];

    for (const id of ids) {
      const answer = this.answers.find((a) => a.id == id);

      if (!answer) continue;

      answers.push(answer);
    }

    this.selectedAnswer = answers;
  }

  getAnswer(): AnswerUserQuestionMultiple | null {
    return this.selectedAnswer;
  }

  isAnswered(): boolean {
    return this.selectedAnswer ? this.selectedAnswer.length > 0 : false;
  }

  resetAnswer(): void {
    this.selectedAnswer = null;
  }

  setMarked(marked: FeedbackAnswerQuestionMultiple): void {
    this.marked = marked;
  }

  getMarked(): FeedbackAnswerQuestionMultiple | null {
    return this.marked;
  }

  isMarked(): boolean {
    return !(this.marked == null);
  }

  correctState(): CorrectType {
    if (!this.isAnswered()) return null;

    const points: number = this.getPoints();
    const maxPoints: number = this.getMaxPoints();

    if (points == 0) return false;
    return points == maxPoints ? true : 'partially';
  }

  toJSON(): QuestionMultipleSerialized {
    return {
      marked: this.marked,
      selectedAnswer: this.selectedAnswer,
    };
  }

  importAnswer(answer: ExportAnswerMultiple): void {
    if (answer.ids) {
      this.selectedAnswer = answer.ids.map((id) => ({ id }));
    } else {
      this.selectedAnswer = null;
    }
  }

  exportAnswer(): ExportAnswerMultiple {
    return {
      ids: this.selectedAnswer ? this.selectedAnswer.map((a) => a.id) : null,
    };
  }
}
